import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Card from '../components/common/Card'
import Spinner from '../components/common/Spinner'
import { faDigits } from '../lib/numerals'
import { chartBars, weekSummary, weekdayNames } from '../lib/progress'
import { classifyFailure } from '../services/api'
import { myStats } from '../services/lessons'
import { useAuthStore } from '../store/authStore'

const BAR_TONE = { peak: 'bg-brand-600', some: 'bg-brand-50', none: 'bg-track' }

function StatTile({ value, label, tone = 'text-ink' }) {
  return (
    <div className="flex flex-col items-center gap-1.5 card-soft rounded-tile px-2.5 py-3.5">
      <span className={`text-[21px] font-bold ${tone}`}>{value}</span>
      <span className="text-center text-[10.5px] text-ink-2">{label}</span>
    </div>
  )
}

/**
 * The seven-day chart of design page 9: a bar per weekday, Saturday first,
 * scaled to the week's own peak; the week's total sits in the header
 * because the bars cannot carry an absolute scale — a five-minute week
 * and a fifty-minute one draw identically.
 */
export function WeeklyChart({ week }) {
  const { t } = useTranslation('progressPage')
  const bars = chartBars(week)
  const total = bars.reduce((sum, bar) => sum + bar.minutes, 0)
  return (
    <Card pad="p-[18px]" className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[13.5px] font-bold text-ink">{t('chart.heading')}</h2>
        <span className="text-[11.5px] text-ink-3">{t('minutesSuffix', { count: faDigits(total) })}</span>
      </div>
      <div className="flex h-[126px] items-end gap-2" role="img" aria-label={t('chart.ariaLabel', { total: faDigits(total) })}>
        {bars.map((bar) => (
          <div key={bar.day} className="flex h-full flex-1 flex-col items-center justify-end gap-2" aria-hidden="true">
            <span className="text-[10px] text-ink-3">{bar.label}</span>
            <span className={`block w-full rounded-[7px] transition-[height] duration-200 ${BAR_TONE[bar.tone]}`} style={{ height: `${bar.height}px` }} />
            <span className="text-[10.5px] text-ink-2">{bar.day}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

function ReportRow({ label, value, emphasise = false }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[12.5px] text-ink-2">{label}</span>
      <span className={`text-[13px] font-semibold ${emphasise ? 'text-brand-600' : 'text-ink'}`}>{value}</span>
    </div>
  )
}

/**
 * «گزارش این هفته»: total, best day, active days, then the streak. An empty
 * week has no best day (`lib/progress.js`), so it gets one sentence instead
 * of three rows about nothing; the streak rows stay, because a streak can
 * be alive on the first day of a week.
 */
export function WeekReport({ week, streakCurrent, streakLongest }) {
  const { t } = useTranslation('progressPage')
  const summary = weekSummary(week)
  return (
    <Card pad="p-4" className="space-y-3.5">
      <h2 className="text-xs font-semibold text-ink-3">{t('report.heading')}</h2>
      {summary.bestDayIndex === null ? (
        <p className="text-[13px] leading-[1.95] text-ink-2">{t('report.empty')}</p>
      ) : (
        <>
          <ReportRow label={t('report.totalLabel')} value={t('minutesSuffix', { count: faDigits(summary.totalMinutes) })} />
          <ReportRow label={t('report.bestDayLabel')} value={t('report.bestDayValue', { minutes: faDigits(summary.bestDayMinutes), day: weekdayNames()[summary.bestDayIndex] })} />
          <ReportRow label={t('report.activeDaysLabel')} value={t('report.activeDaysValue', { count: faDigits(summary.activeDays) })} />
        </>
      )}
      <ReportRow label={t('report.streakCurrentLabel')} value={t('daysSuffix', { count: faDigits(streakCurrent ?? 0) })} emphasise />
      <ReportRow label={t('report.streakLongestLabel')} value={t('daysSuffix', { count: faDigits(streakLongest ?? 0) })} />
    </Card>
  )
}

/**
 * The progress section (design page 9): the seven-day chart, the week's
 * report and the door to the leaderboard, from a `me/stats` answer handed
 * in. Since V3-w it renders inside the profile page, which already has
 * the stats; the `Progress` page below keeps drawing it on its own for
 * the old `/progress` address.
 */
export function ProgressSection({ stats }) {
  const { t } = useTranslation('progressPage')
  return (
    <>
      <WeeklyChart week={stats.weekMinutes} />

      <WeekReport week={stats.weekMinutes} streakCurrent={stats.streakCurrent} streakLongest={stats.streakLongest} />

      <Link to="/leaderboard" className="flex h-14 items-center justify-between card-soft rounded-tile px-4 text-sm font-medium text-ink transition hover:bg-surface-2">
        <span>{t('leaderboardLink.label')}</span>
        <span className="text-xs text-ink-3">{t('leaderboardLink.hint')}</span>
      </Link>
    </>
  )
}

/**
 * The progress page (design page 9, `progress`; P-progress of the go-live
 * plan). All of it is `me/stats`; until the answer arrives the page shows
 * a spinner, never sample numbers. It deliberately does not repeat the
 * archive's list: this page answers "how much am I practising", the
 * profile answers "who am I", the archive "what did I say". Since V3-w
 * `/progress` redirects to the profile, where `ProgressSection` lives;
 * the page stays as the section's standalone frame.
 */
export default function Progress() {
  const { t } = useTranslation('progressPage')
  const xp = useAuthStore((s) => s.user?.xp ?? 0)
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    setError(null)
    myStats()
      .then((data) => {
        if (!cancelled) setStats(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [reload])

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <h1 className="text-[23px] font-bold tracking-[-0.3px] text-ink">{t('title')}</h1>

      {error && !stats ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('errors.offline') : t('errors.server')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('errors.retry')}
          </Button>
        </div>
      ) : !stats ? (
        <Spinner label={t('loadingLabel')} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <StatTile value={faDigits(stats.attemptCount ?? 0)} label={t('stats.attempts')} />
            <StatTile value={faDigits(xp)} label={t('stats.xp')} tone="text-good" />
            <StatTile value={faDigits(stats.totalMinutes ?? 0)} label={t('stats.minutes')} />
          </div>

          <ProgressSection stats={stats} />
        </>
      )}
    </div>
  )
}
